import { JSX, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./Vulnerability.css";
import FilterPanelLevel2 from "../Filter/FilterPanelLevel2/FilterPanelLevel2.tsx";
import Title from "../Title/Title.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import TopVulnerabilityCharts from "./TopVulnerabilityCharts/TopVulnerabilityCharts.tsx";
import { api } from "../../api/client.tsx";
import { Loader } from "../../Loading/Loading.tsx";
import Breadcrumbs from "../BreadcrumbNav/Breadcrumbs.tsx";
import ChainsVulnerability from "../Vulnerability/ChainsVulnerability/ChainsVulnerability.tsx";

type Filters = {
  Severity: string[];
  PackageName: string;
  Layer: number | "";
  LifespanMin: number | "";
  ChainsContains: string;
};

type VulnRow = {
  image: string;
  name: string;
  version: string;
  cve: string;
  severity: string;
  layer: number | string;
  link: string;
  lifespan_days?: number | null;
  chains?: string | null;
};

function normalizeSeverity(s: unknown): string {
  const v = String(s ?? "").trim();
  if (!v) return "";
  const lower = v.toLowerCase();
  if (lower.startsWith("negl")) return "Negligible";
  if (lower.startsWith("low")) return "Low";
  if (lower.startsWith("med")) return "Medium";
  if (lower.startsWith("high")) return "High";
  if (lower.startsWith("crit")) return "Critical";
  if (lower.startsWith("unk")) return "Unknown";
  return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
}

function isAbortError(err: any): boolean {
  if (!err) return false;
  const name = String(err.name || "").toLowerCase();
  const msg = String(err.message || "").toLowerCase();
  return (
    name === "aborterror" ||
    msg.includes("aborted") ||
    msg.includes("canceled") ||
    msg.includes("cancelled")
  );
}

function rawTailFromImageId(id: string): string {
  const s = decodeURIComponent(String(id || "").trim());
  const us = s.indexOf("_");
  return us < 0 ? s : s.slice(us + 1);
}

function buildVersionKeyCandidates(id: string): string[] {
  const raw = rawTailFromImageId(id);
  const a = raw;
  const b = raw.replace(/_/g, "-");
  const c = raw.replace(/-/g, "_");
  return Array.from(new Set([a, b, c])).filter(Boolean);
}

function ThWithHelp({
  title,
  tip,
  id,
}: {
  title: string;
  tip: string;
  id: string;
}) {
  return (
    <div className="v-th th-with-q">
      <span className="v-th-text th-title">{title}</span>
      <button
        type="button"
        className="qmark"
        aria-label={`What is "${title}"?`}
        aria-describedby={id}
        onClick={(e) => e.preventDefault()}
      >
        <span aria-hidden="true">?</span>
        <span id={id} role="tooltip" className="qmark-tip">
          {tip}
        </span>
      </button>
    </div>
  );
}

const COL_DESC_L2 = {
  cve: "Common Vulnerability Enumeration (CVE) ID found in this image version.",
  severity:
    "Severity level of the vulnerability (Low, Medium, High, Critical, etc.).",
  pkg: "Vulnerable package and installed version where the CVE is found.",
  layer: "Data layer of the image where the CVE is found.",
  chains:
    "List of colocated CVEs that the current CVE can be chained with in the same image.",
  link: "External reference (usually NVD) with full vulnerability details.",
};

const SEARCHABLE_COLUMNS_L2 = [
  { key: "cve", label: "CVE ID" },
  { key: "name", label: "Vulnerable Package" },
  { key: "version", label: "Version" },
  { key: "layer", label: "Layer Number" },
  { key: "severity", label: "Severity" },
  { key: "image", label: "Image ID" },
];

const NUMERIC_KEYS_L2 = new Set<string>(["layer"]);

const getColumnLabelL2 = (key: string) =>
  SEARCHABLE_COLUMNS_L2.find((c) => c.key === key)?.label ?? key;

export default function Vulnerability() {
  const { imageId: rawImageId } = useParams();
  const imageId = decodeURIComponent(rawImageId || "");
  const isAll = imageId.toLowerCase() === "all";

  const [searchColumn, setSearchColumn] = useState<string>("");
  const [searchInput, setSearchInput] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const navigate = useNavigate();

  const [filters, setFilters] = useState<Filters>({
    Severity: [],
    PackageName: "",
    Layer: "",
    LifespanMin: "",
    ChainsContains: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [total, setTotal] = useState(0);

  const [rows, setRows] = useState<VulnRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [l2Key, setL2Key] = useState<string>(isAll ? "" : imageId);

  const [allPackageNames, setAllPackageNames] = useState<string[]>([]);
  const [allLayerNums, setAllLayerNums] = useState<number[]>([]);
  const optionsPrefetchedRef = useRef(false);

  const packageNameOptions = useMemo(() => {
    if (allPackageNames.length)
      return [...allPackageNames].sort((a, b) => a.localeCompare(b));
    const s = new Set<string>();
    rows.forEach((r) => r.name && s.add(r.name));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [allPackageNames, rows]);

  const layerOptions = useMemo(() => {
    if (allLayerNums.length) return [...allLayerNums].sort((a, b) => a - b);
    const s = new Set<number>();
    rows.forEach((r) => {
      const n = Number(r.layer);
      if (Number.isFinite(n)) s.add(n);
    });
    return Array.from(s).sort((a, b) => a - b);
  }, [allLayerNums, rows]);

  // reset when image changes
  useEffect(() => {
    setSearchColumn("");
    setSearchInput("");
    setActiveSearchTerm("");
    setFilters({
      Severity: [],
      PackageName: "",
      Layer: "",
      LifespanMin: "",
      ChainsContains: "",
    });
    setCurrentPage(1);
    setRows([]);
    setTotal(0);
    setError(null);
    setL2Key(isAll ? "" : imageId);
    setAllPackageNames([]);
    setAllLayerNums([]);
    optionsPrefetchedRef.current = false;
  }, [imageId, isAll]);

  // map L1 id -> L2 key
  useEffect(() => {
    if (isAll || !imageId) return;
    let canceled = false;
    (async () => {
      try {
        const l1 = await api.level1.summary(imageId);
        if (canceled) return;
        const candidate =
          (l1?.Image as string) ??
          (l1?.image as string) ??
          (l1?.ImageId as string) ??
          (l1?.image_id as string) ??
          (l1?.L2_Image as string) ??
          (l1?.l2_image as string);
        if (
          candidate &&
          typeof candidate === "string" &&
          candidate.trim()
        ) {
          setL2Key(candidate.trim());
        }
      } catch (err) {
        if (canceled || isAbortError(err)) return;
        // non-fatal: we can still fall back to imageId
      }
    })();
    return () => {
      canceled = true;
    };
  }, [imageId, isAll]);

  async function prefetchAllOptions(force = false) {
    if (isAll) return;
    if (optionsPrefetchedRef.current && !force) return;
    const key = l2Key || imageId;
    if (!key) return;
    try {
      const res = await api.level2.options(key);
      const pkgsRaw: any[] = Array.isArray(res?.packages) ? res.packages : [];
      const layersRaw: any[] = Array.isArray(res?.layers) ? res.layers : [];
      const names = pkgsRaw
        .map((p) => String(p ?? "").trim())
        .filter((p) => p.length > 0);
      const layers = layersRaw
        .map((x) => Number(x))
        .filter((n) => Number.isFinite(n)) as number[];
      setAllPackageNames(Array.from(new Set(names)));
      setAllLayerNums(Array.from(new Set(layers)));
      optionsPrefetchedRef.current = true;
    } catch (err) {
      if (isAbortError(err)) return;
    }
  }

  useEffect(() => {
    prefetchAllOptions().catch(() => {});
  }, [l2Key, isAll]);

  const handlePackagesOpen = () => {
    prefetchAllOptions().catch(() => {});
  };

  useEffect(() => {
    let canceled = false;

    (async () => {
      setLoading(true);
      setError(null);

      const layerNum =
        filters.Layer === "" ? undefined : Number(filters.Layer);
      const lifeMin =
        filters.LifespanMin === "" ? undefined : Number(filters.LifespanMin);

      const queryFilters: any = {
        severity: filters.Severity.length ? filters.Severity : undefined,
        package_name: filters.PackageName || undefined,
        package: filters.PackageName || undefined,
        layer: Number.isFinite(layerNum as number)
          ? (layerNum as number)
          : undefined,
        lifespan_min: Number.isFinite(lifeMin as number)
          ? (lifeMin as number)
          : undefined,
        chains_contains: filters.ChainsContains || undefined,
      };

      try {
        let pageData: any = null;

        if (isAll) {
          pageData = await api.level2.allPaged(
            currentPage,
            itemsPerPage,
            queryFilters
          );
        } else {
          const candidates = buildVersionKeyCandidates(l2Key || imageId);
          let found = false;
          for (const key of candidates) {
            try {
              const pd = await api.level2.byVersionPaged(
                key,
                currentPage,
                itemsPerPage,
                queryFilters
              );
              // @ts-ignore
              if (pd && Number(pd.total ?? 0) > 0) {
                pageData = pd;
                found = true;
                break;
              }
              if (!pageData) pageData = pd;
            } catch (err) {
              if (isAbortError(err)) return;
            }
          }
          if (!found) {
            try {
              pageData = await api.level2.byImagePaged(
                l2Key || imageId,
                currentPage,
                itemsPerPage,
                queryFilters
              );
            } catch (err) {
              if (isAbortError(err)) return;
            }
          }
        }

        if (canceled) return;

        const items = Array.isArray(pageData?.items) ? pageData.items : [];
        let mapped: VulnRow[] = items.map((v: any) => {
          const cve = v.Vulnerability_ID ?? v.vulnerability_id ?? "";
          const chains = v.Chains ?? v.chains ?? v.chained_with ?? null;

          return {
            image: v.Image ?? v.image ?? (isAll ? "" : l2Key || imageId),
            name: v.Package ?? v.package ?? "",
            version: v.Version_Installed ?? v.version_installed ?? "",
            cve,
            severity: normalizeSeverity(v.Severity ?? v.severity ?? ""),
            layer: v.Layer ?? v.layer ?? "",
            link: cve ? `https://nvd.nist.gov/vuln/detail/${cve}` : "#",
            chains:
              typeof chains === "string"
                ? chains
                : Array.isArray(chains)
                ? chains.join(", ")
                : null,
          };
        });

        if (filters.PackageName) {
          mapped = mapped.filter((r) => r.name === filters.PackageName);
        }

        setRows(mapped);
        const backendTotal = Number(pageData?.total ?? 0);
        setTotal(filters.PackageName ? mapped.length : backendTotal);
        setLoading(false);
      } catch (err: any) {
        if (canceled || isAbortError(err)) return;
        setRows([]);
        setTotal(0);
        setLoading(false);
        setError("Failed to load vulnerabilities.");
      }
    })();

    return () => {
      canceled = true;
    };
  }, [isAll, l2Key, imageId, currentPage, itemsPerPage, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, activeSearchTerm, searchColumn, isAll, l2Key, imageId]);

  const filteredRows = useMemo(() => {
    const col = searchColumn;
    const termRaw = activeSearchTerm.trim();
    const term = termRaw.toLowerCase();
    if (!col || !termRaw) return rows;

    return rows.filter((r) => {
      let value: any = (r as any)[col];

      if (NUMERIC_KEYS_L2.has(col)) {
        const n = Number(termRaw);
        if (Number.isFinite(n)) {
          const valNum = Number(value);
          if (Number.isFinite(valNum)) {
            return valNum === n;
          }
        }
        return String(value ?? "").toLowerCase().includes(term);
      }

      return String(value ?? "").toLowerCase().includes(term);
    });
  }, [rows, searchColumn, activeSearchTerm]);

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleSearchClick = () => {
    setActiveSearchTerm(searchInput.trim());
  };

  const repoKey = imageId.includes("_") ? imageId.split("_")[0] : imageId;
  const versionPart = imageId.includes("_")
    ? imageId.split("_").slice(1).join("_")
    : imageId;

  return (
    <div className="vuln-container">
      <div className="vuln-title-section">
        <h1 className="vuln-title">
          <Title />
          <p className="p-warning">
            Due to the large volume of data being processed, loading may take a
            few moments. Thank you for your patience.
          </p>
        </h1>
        <h3 className="vuln-image-heading"></h3>
      </div>

      <div className="info-btn-div">
        <button
          className="side-info-btn"
          onClick={() => navigate("/info")}
          aria-label="Open Information"
          title="Information"
        >
          <FontAwesomeIcon icon={faCircleInfo} />
          <span className="side-info-label">Info</span>
        </button>
      </div>

      <div className="breadcrumb-div">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: repoKey, to: `/versions/${repoKey}` },
            { label: versionPart || imageId },
          ]}
        />
      </div>

      {!isAll && (
        <div className="severity-chains-row">
          <TopVulnerabilityCharts selectedImage={l2Key || imageId} />
          <ChainsVulnerability imageId={imageId} l2ImageId={l2Key || imageId} />
        </div>
      )}

      <div className="vuln-layout">
        <div className="vuln-left-panel">
          <div className="vuln-controls">
            <select
              className="vuln-search-box"
              value={searchColumn}
              onChange={(e) => {
                const val = e.target.value;
                setSearchColumn(val);
                setSearchInput("");
                setActiveSearchTerm("");
              }}
            >
              <option value="">Select column…</option>
              {SEARCHABLE_COLUMNS_L2.map((col) => (
                <option key={col.key} value={col.key}>
                  {col.label}
                </option>
              ))}
            </select>

            {searchColumn && (
              <div className="vuln-search-row">
                <input
                  className="vuln-search-box"
                  type="text"
                  placeholder={`Search in ${getColumnLabelL2(
                    searchColumn
                  )}…`}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchClick()}
                />
                <button className="vuln-CSVButton" onClick={handleSearchClick}>
                  Search <FontAwesomeIcon icon={faMagnifyingGlass} />
                </button>
              </div>
            )}
          </div>

          <div className="vuln-filterpanel">
            <FilterPanelLevel2
              filters={filters}
              setFilters={setFilters}
              packageOptions={packageNameOptions}
              layerOptions={layerOptions}
              onPackagesOpen={handlePackagesOpen}
            />
          </div>
        </div>

        <div className="vuln-right-panel">
          <div className="table-titlebar" role="heading" aria-level={2}>
            <span className="table-title">List of Vulnerabilities</span>
          </div>

          <div className="vuln-table-wrapper">
            <table className="vuln-table">
              <thead>
                <tr>
                  <th>
                    <ThWithHelp
                      title="CVE ID"
                      id="th-cve"
                      tip={COL_DESC_L2.cve}
                    />
                  </th>
                  <th>
                    <ThWithHelp
                      title="Vulnerable Package"
                      id="th-pkg"
                      tip={COL_DESC_L2.pkg}
                    />
                  </th>
                  <th>
                    <ThWithHelp
                      title="Version"
                      id="th-ver"
                      tip="Installed package version in the image."
                    />
                  </th>
                  <th>
                    <ThWithHelp
                      title="Layer Number"
                      id="th-layer"
                      tip={COL_DESC_L2.layer}
                    />
                  </th>
                  <th>
                    <ThWithHelp
                      title="Severity"
                      id="th-sev"
                      tip={COL_DESC_L2.severity}
                    />
                  </th>
                  <th>
                    <ThWithHelp
                      title="Link"
                      id="th-link"
                      tip={COL_DESC_L2.link}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ textAlign: "left", padding: "1rem" }}
                    >
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loading && error && (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        textAlign: "center",
                        padding: "1rem",
                        color: "#941C2F",
                      }}
                    >
                      {error}
                    </td>
                  </tr>
                )}
                {!loading && !error && filteredRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ textAlign: "center", padding: "1rem" }}
                    >
                      No vulnerabilities found.
                    </td>
                  </tr>
                )}
                {!loading &&
                  !error &&
                  filteredRows.map((item, idx) => (
                    <tr key={`${item.cve}-${idx}`}>
                      <td>{item.cve}</td>
                      <td>{item.name}</td>
                      <td>{item.version}</td>
                      <td>{item.layer}</td>
                      <td>{item.severity}</td>
                      <td>
                        {item.link && item.link !== "#" ? (
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {!loading && !error && total > 0 && (
            <div className="pagination-controls">
              <button
                className="pagination-link"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              {(() => {
                const pages: JSX.Element[] = [];
                const windowSize = 5;
                let startPage = Math.max(
                  1,
                  currentPage - Math.floor(windowSize / 2)
                );
                let endPage = startPage + windowSize - 1;
                if (endPage > totalPages) {
                  endPage = totalPages;
                  startPage = Math.max(1, endPage - windowSize + 1);
                }
                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      className="pagination-link"
                      onClick={() => goToPage(1)}
                    >
                      1
                    </button>
                  );
                  if (startPage > 2)
                    pages.push(<span key="start-ellipsis">…</span>);
                }
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={`pagination-link ${
                        currentPage === i ? "active" : ""
                      }`}
                      onClick={() => goToPage(i)}
                    >
                      {i}
                    </button>
                  );
                }
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1)
                    pages.push(<span key="end-ellipsis">…</span>);
                  pages.push(
                    <button
                      key={totalPages}
                      className="pagination-link"
                      onClick={() => goToPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  );
                }
                return pages;
              })()}
              <button
                className="pagination-link"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
